import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../config';
import { Play, Pause, X, Download, SkipBack, SkipForward } from 'lucide-react';

const CallRecording = ({ callId, isAvailable = true }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [recordingInfo, setRecordingInfo] = useState(null);
  const [infoLoading, setInfoLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  const getAuthHeader = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`
    };
  };

  // Carica le informazioni sulla registrazione all'avvio
  useEffect(() => {
    const fetchRecordingInfo = async () => {
      setInfoLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/calls/${callId}/recording-info`, {
          headers: getAuthHeader()
        });

        if (!response.ok) {
          console.error(`Fehler beim Abrufen der Aufnahmeinformationen: ${response.status}`);
          return;
        }

        const data = await response.json();
        setRecordingInfo(data);
      } catch (error) {
        console.error('Fehler:', error);
      } finally {
        setInfoLoading(false);
      }
    };

    fetchRecordingInfo();
  }, [callId]);

  // Aggiorna il tempo corrente durante la riproduzione
  useEffect(() => {
    const updateTime = () => {
      if (audioRef.current) {
        setCurrentTime(audioRef.current.currentTime);
      }
    };

    if (audioRef.current) {
      audioRef.current.addEventListener('timeupdate', updateTime);
      audioRef.current.addEventListener('loadedmetadata', () => {
        setDuration(audioRef.current.duration);
      });
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('timeupdate', updateTime);
      }
    };
  }, [audioUrl]);

  const playRecording = async () => {
    // Verifica se l'utente ha il permesso di accedere alla registrazione
    if (!recordingInfo || !recordingInfo.has_permission || !recordingInfo.is_available) {
      return;
    }
    
    if (audioUrl) {
      if (audioRef.current) {
        if (isPlaying) {
          audioRef.current.pause();
        } else {
          audioRef.current.play();
        }
      }
      setIsPlaying(!isPlaying);
      return;
    }

    setLoading(true);

    try {
      // Ottieni l'URL della registrazione
      const response = await fetch(`${API_BASE_URL}/calls/${callId}/recording`, {
        headers: getAuthHeader()
      });

      if (!response.ok) {
        console.error(`Fehler beim Abrufen der Aufnahme: ${response.status}`);
        setLoading(false);
        return;
      }

      // Crea un URL per il blob audio
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      setIsPlaying(true);
    } catch (error) {
      console.error('Fehler:', error);
    } finally {
      setLoading(false);
    }
  };

  const stopPlayback = () => {
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
    }
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
  };

  // Funzione per scaricare la registrazione
  const downloadRecording = async () => {
    if (!audioUrl) {
      // Se l'audio non è ancora stato caricato, lo carichiamo
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/calls/${callId}/recording`, {
          headers: getAuthHeader()
        });

        if (!response.ok) {
          console.error(`Fehler beim Abrufen der Aufnahme: ${response.status}`);
          setLoading(false);
          return;
        }

        // Crea un URL per il blob audio
        const blob = await response.blob();
        
        // Crea un link per il download
        const downloadUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `call-recording-${callId}.wav`; // Nome del file di default
        document.body.appendChild(a);
        a.click();
        
        // Pulisci
        setTimeout(() => {
          document.body.removeChild(a);
          URL.revokeObjectURL(downloadUrl);
        }, 100);
      } catch (error) {
        console.error('Fehler beim Herunterladen:', error);
      } finally {
        setLoading(false);
      }
    } else {
      // Se l'audio è già caricato, usiamo l'URL esistente
      const a = document.createElement('a');
      a.href = audioUrl;
      a.download = `call-recording-${callId}.wav`;
      document.body.appendChild(a);
      a.click();
      
      // Pulisci
      setTimeout(() => {
        document.body.removeChild(a);
      }, 100);
    }
  };

  // Funzione per andare avanti di 10 secondi
  const skipForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime += 10;
    }
  };

  // Funzione per andare indietro di 10 secondi
  const skipBackward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime -= 10;
    }
  };

  // Formatta il tempo in formato mm:ss
  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Determina se il pulsante deve essere disabilitato
  const isButtonDisabled = () => {
    if (infoLoading || loading) return true;
    if (!recordingInfo) return true;
    return !recordingInfo.has_permission || !recordingInfo.is_available;
  };

  return (
    <div className="mt-2">
      {audioUrl ? (
        <div className="space-y-2">
          {/* Audio nascosto per controllo programmatico */}
          <audio
            ref={audioRef}
            src={audioUrl}
            className="hidden"
            autoPlay={isPlaying}
            onEnded={() => setIsPlaying(false)}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
          />
          
          {/* Interfaccia stile WhatsApp */}
          <div className="bg-gray-100 rounded-lg p-3 w-full">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2 flex-grow">
                <button
                  onClick={() => {
                    if (audioRef.current) {
                      if (isPlaying) {
                        audioRef.current.pause();
                      } else {
                        audioRef.current.play();
                      }
                      setIsPlaying(!isPlaying);
                    }
                  }}
                  className="w-8 h-8 flex-shrink-0 flex items-center justify-center rounded-full bg-indigo-600 text-white hover:bg-indigo-700"
                  title={isPlaying ? "Pause" : "Abspielen"}
                >
                  {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                </button>
                
                {/* Barra di avanzamento */}
                <div className="relative w-full h-1.5 bg-gray-300 rounded-full overflow-hidden flex-grow mx-2">
                  <div 
                    className="absolute top-0 left-0 h-full bg-indigo-600 rounded-full"
                    style={{ width: `${(currentTime / duration) * 100}%` }}
                  ></div>
                </div>
              </div>
              
              {/* Tempo */}
              <div className="text-xs text-gray-600 ml-2 flex-shrink-0 min-w-[40px] text-right">
                {formatTime(currentTime)}
              </div>
            </div>
            
            {/* Controlli aggiuntivi */}
            <div className="flex items-center justify-between mt-1">
              <div className="flex items-center space-x-1">
                <button
                  onClick={skipBackward}
                  className="p-1 text-gray-600 hover:text-indigo-600"
                  title="10 Sekunden zurück"
                >
                  <SkipBack className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={skipForward}
                  className="p-1 text-gray-600 hover:text-indigo-600"
                  title="10 Sekunden vorwärts"
                >
                  <SkipForward className="h-3.5 w-3.5" />
                </button>
              </div>
              
              <div className="flex items-center space-x-1">
                <button
                  onClick={downloadRecording}
                  className="p-1 text-gray-600 hover:text-green-600"
                  title="Herunterladen"
                >
                  <Download className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={stopPlayback}
                  className="p-1 text-gray-600 hover:text-red-600"
                  title="Schließen"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <button
          onClick={playRecording}
          disabled={isButtonDisabled()}
          className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded ${
            infoLoading || loading
              ? 'bg-gray-100 text-gray-500 cursor-wait'
              : isButtonDisabled()
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed opacity-70'
                : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
          }`}
        >
          {infoLoading || loading ? (
            <svg className="animate-spin h-4 w-4 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          ) : (
            <Play className="h-4 w-4 mr-1" />
          )}
          {infoLoading || loading ? 'Laden...' : 'Aufnahme'}
        </button>
      )}
    </div>
  );
};

export default CallRecording;
